import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import LuggageOutlinedIcon from "@mui/icons-material/LuggageOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { localizedDate } from "../../../../utils/dayjs";
import { useI18n } from "../../../hooks/useI18n";
import { useTravels } from "../../../hooks/useTravels";
import { theme } from "../../../layout/Theme";
import { TravelQuestionnaireAnswers } from "../travel/TravelQuestionnaireAnswers";
import { getTravelDestinationNames, getTravelPeriod, isTravelPast } from "../travel/travelTypes";
import { IframeDiagnostic } from "./IframeDiagnostic";

export function TravelDiagnosticSelector() {
  const { locale, t } = useI18n();
  const { id } = useParams() as { id: string };
  const { travels } = useTravels(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTravelId = searchParams.get("travel") ?? "";
  const requestedTravel = travels.find(({ id }) => id === requestedTravelId);
  const selectedTravelId = requestedTravel && !isTravelPast(requestedTravel.travel) ? requestedTravelId : "";
  const selectedTravel = travels.find(({ id }) => id === selectedTravelId);
  const selectedTravelIsPast = selectedTravel ? isTravelPast(selectedTravel.travel) : false;
  const selectedTravelPeriod = selectedTravel ? getTravelPeriod(selectedTravel.travel) : undefined;
  const selectedTravelDestinations = selectedTravel ? getTravelDestinationNames(selectedTravel.travel, t) : undefined;
  const selectedStayingCondition = selectedTravel?.travel.journey.staying_conditions;

  const handleChange = (event: SelectChangeEvent) => {
    const travelId = event.target.value;
    setSearchParams(travelId ? { travel: travelId } : {});
  };

  const getLabel = (travelId: string) => {
    const travelRecord = travels.find(({ id }) => id === travelId);
    if (!travelRecord) return t("travel.diagnostic_without_travel");

    const destinations = getTravelDestinationNames(travelRecord.travel, t);
    return `${destinations}${isTravelPast(travelRecord.travel) ? ` — ${t("travel.past")}` : ""}`;
  };

  return (
    <>
      {travels.length > 0 && (
        <Paper variant="outlined" sx={{ mx: 1, mb: 2, p: { xs: 2, md: 2.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={2}>
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: "50%",
                color: theme.palette.primary[500],
                backgroundColor: theme.palette.primary[100],
              }}
            >
              <FlightTakeoffIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t("travel.diagnostic_panel_title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("travel.diagnostic_panel_description")}
              </Typography>
            </Box>
            <FormControl size="small" sx={{ width: { xs: "100%", md: 380 }, flexShrink: 0 }}>
              <InputLabel id="travel-diagnostic-selector-label">{t("travel.diagnostic_selector_label")}</InputLabel>
              <Select
                labelId="travel-diagnostic-selector-label"
                value={selectedTravelId}
                label={t("travel.diagnostic_selector_label")}
                onChange={handleChange}
                renderValue={getLabel}
                sx={
                  selectedTravelIsPast
                    ? {
                        backgroundColor: theme.palette.neutral[100],
                        color: theme.palette.neutral[500],
                        filter: "grayscale(1)",
                      }
                    : undefined
                }
              >
                <MenuItem value="">{t("travel.diagnostic_without_travel")}</MenuItem>
                {travels.map((travelRecord) => {
                  const period = getTravelPeriod(travelRecord.travel);
                  const past = isTravelPast(travelRecord.travel);
                  const destinations = getTravelDestinationNames(travelRecord.travel, t);
                  const periodLabel = period
                    ? t("travel.period", {
                        startDate: localizedDate(period.startDate, locale),
                        endDate: localizedDate(period.endDate, locale),
                      })
                    : undefined;

                  return (
                    <MenuItem
                      key={travelRecord.id}
                      value={travelRecord.id}
                      disabled={past}
                      sx={
                        past
                          ? {
                              backgroundColor: theme.palette.neutral[100],
                              color: theme.palette.neutral[500],
                              filter: "grayscale(1)",
                              opacity: 0.65,
                            }
                          : undefined
                      }
                    >
                      <ListItemText
                        primary={`${destinations}${past ? ` — ${t("travel.past")}` : ""}`}
                        secondary={periodLabel}
                        slotProps={{ secondary: past ? { color: "inherit" } : undefined }}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>

          {selectedTravel && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 4 }}
                color={selectedTravelIsPast ? "text.disabled" : "text.primary"}
              >
                <SummaryItem
                  icon={<PlaceOutlinedIcon fontSize="small" />}
                  label={t("travel.summary_destinations")}
                  value={selectedTravelDestinations ?? t("travel.no_destination")}
                />
                {selectedTravelPeriod && (
                  <SummaryItem
                    icon={<CalendarMonthOutlinedIcon fontSize="small" />}
                    label={t("travel.summary_period")}
                    value={t("travel.period", {
                      startDate: localizedDate(selectedTravelPeriod.startDate, locale),
                      endDate: localizedDate(selectedTravelPeriod.endDate, locale),
                    })}
                  />
                )}
                {selectedStayingCondition && (
                  <SummaryItem
                    icon={<LuggageOutlinedIcon fontSize="small" />}
                    label={t("travel.summary_stay")}
                    value={t(`travel.staying_conditions.${selectedStayingCondition}`)}
                  />
                )}
                {selectedTravelIsPast && <Chip label={t("travel.past")} size="small" sx={{ alignSelf: "center" }} />}
              </Stack>
              <TravelQuestionnaireAnswers
                answers={selectedTravel.travel.conditions}
                muted={selectedTravelIsPast}
              />
            </>
          )}
        </Paper>
      )}
      <IframeDiagnostic key={selectedTravelId || "without-travel"} travel={selectedTravel?.travel} />
    </>
  );
}

function SummaryItem({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", color: "inherit" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}
